// src/pages/DashboardHelper.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";

import { useAuth } from "../context/AuthContext";
import { useHelperOnboarding } from "../context/HelperOnboardingContext";
import { normalizeRole, ROLE_VALUE, getRoleLabel } from "../constants/roleLabels";
import { db } from "../firebase";
import { compareBySeverity } from "../constants/severity";
import { formatDuration } from "../utils/duration";
import { waitingOnViewerCount } from "../utils/queueMetrics";
import { logger } from "../utils/logger";

import { getSuggestedOpenProblemsForHelper } from "../services/matchingService";
import { takeJob } from "../services/problemService";
import { useIncidentQueue } from "../hooks/useIncidentQueue";

import OperationsShell from "../components/dashboard/shell/OperationsShell";
import OperationsSummary from "../components/dashboard/OperationsSummary";
import IncidentQueueTable from "../components/dashboard/IncidentQueueTable";
import InvitationCard from "../components/dashboard/InvitationCard";
import DashboardCard from "../components/dashboard/DashboardCard";
import ClaimBriefingModal from "../components/dashboard/ClaimBriefingModal";
import ConversationDrawer from "../components/dashboard/ConversationDrawer";
import IncidentReportView from "../components/workspace/IncidentReportView";
import IncidentReportForm from "../components/workspace/IncidentReportForm";

/**
 * The expert's operations console.
 *
 * Structured around the three questions an on-call engineer actually asks:
 * what am I already responsible for, what is unclaimed, and who asked for me by
 * name. The previous version had four tabs, two of which ("Available Incidents"
 * and "Suggested For You") drew from the same pool of open incidents and simply
 * ordered it differently, so the same incident appeared twice with two
 * different priorities.
 */

function SectionHeading({ title, count, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-base font-semibold text-slate-900">
        {title}
        {typeof count === "number" && (
          <span className="ml-2 text-sm font-normal tabular-nums text-slate-500">
            {count}
          </span>
        )}
      </h2>
      {children}
    </div>
  );
}

function EmptyPanel({ title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      {body && (
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{body}</p>
      )}
    </div>
  );
}

function DashboardHelper() {
  const { currentUser, currentUserRole, logout, ready } = useAuth();
  const { helperRecord, draft, basicInfoComplete } = useHelperOnboarding();
  const navigate = useNavigate();
  const onboardingToastRef = useRef(false);

  const [activeTab, setActiveTab] = useState("overview");
  const [helperProfile, setHelperProfile] = useState(null);
  const [openIncidents, setOpenIncidents] = useState([]);
  const [matchById, setMatchById] = useState({});
  const [invitations, setInvitations] = useState([]);
  const [briefingProblem, setBriefingProblem] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [showConversations, setShowConversations] = useState(false);
  const [reportViewId, setReportViewId] = useState(null);
  const [pendingReportProblem, setPendingReportProblem] = useState(null);

  const {
    active,
    completed,
    summary,
    confirmResolution,
    finalizeResolution,
    closeUnresolved,
    requestRefund,
    dismissIncident,
  } = useIncidentQueue({ currentUser, viewerRole: "expert" });

  useEffect(() => {
    if (!ready) return;
    if (
      !currentUser ||
      normalizeRole(currentUserRole) !== ROLE_VALUE.specialist
    ) {
      navigate("/login");
    }
  }, [currentUser, currentUserRole, ready, navigate]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getDoc(doc(db, "users", currentUser.uid))
      .then((snap) => {
        if (snap.exists()) setHelperProfile({ id: snap.id, ...snap.data() });
      })
      .catch((error) => console.error("Error fetching profile:", error));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!draft || onboardingToastRef.current) return;
    if ((draft.step || 1) < 2) {
      toast.info("Finish onboarding to unlock a personalised incident queue.");
      onboardingToastRef.current = true;
    }
  }, [draft]);

  const expertSkills = useMemo(
    () =>
      (
        helperRecord?.basicInfo?.primarySkills ||
        helperProfile?.skills ||
        []
      ).map((skill) => String(skill).toLowerCase().trim()),
    [helperRecord, helperProfile]
  );
  const skillsKey = expertSkills.join("|");

  // Live view of the unclaimed queue. Kept as a subscription rather than folded
  // into the ranking call below because two experts can be looking at the same
  // incident, and the loser of that race needs to see it disappear.
  useEffect(() => {
    if (!currentUser?.uid || expertSkills.length === 0) {
      setOpenIncidents([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      query(collection(db, "problems"), where("status", "==", "open")),
      (snapshot) => {
        const matching = snapshot.docs
          .map((snap) => ({ id: snap.id, ...snap.data() }))
          .filter((incident) => {
            const tags = (incident.tags || []).map((tag) =>
              String(tag).toLowerCase().trim()
            );
            return tags.some((tag) => expertSkills.includes(tag));
          });

        // Worst first, oldest next. Match quality is evidence for the claim
        // decision, not the queue's ordering principle — a P1 outage outranks a
        // better-matched P3 every time.
        setOpenIncidents(matching.sort(compareBySeverity));
      },
      (error) => console.error("Open incident subscription error:", error)
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, skillsKey]);

  // Match evidence for the rows above, fetched once per skill change.
  useEffect(() => {
    if (!currentUser?.uid || expertSkills.length === 0) return;

    let cancelled = false;
    getSuggestedOpenProblemsForHelper(currentUser.uid, 50)
      .then(({ problems }) => {
        if (cancelled) return;
        setMatchById(
          Object.fromEntries(
            problems.map((entry) => [
              entry.problem.id,
              {
                matchScore: entry.matchScore,
                isStrongMatch: entry.isStrongMatch,
                matchReasons: entry.matchReasons,
              },
            ])
          )
        );
      })
      .catch((error) => {
        logger.log("Could not rank the incident queue:", error.message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, skillsKey]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsubscribe = onSnapshot(
      query(
        collection(db, "invitations"),
        where("helperId", "==", currentUser.uid),
        where("status", "==", "pending")
      ),
      async (snapshot) => {
        const ordered = snapshot.docs.sort(
          (a, b) =>
            (b.data().createdAt?.seconds || 0) -
            (a.data().createdAt?.seconds || 0)
        );

        const hydrated = await Promise.all(
          ordered.map(async (inviteDoc) => {
            const invite = inviteDoc.data();
            try {
              const problemSnap = await getDoc(
                doc(db, "problems", invite.problemId)
              );
              if (!problemSnap.exists()) return null;
              return {
                id: inviteDoc.id,
                ...invite,
                problem: { id: problemSnap.id, ...problemSnap.data() },
              };
            } catch (error) {
              console.error("Could not load invited incident:", error);
              return null;
            }
          })
        );

        setInvitations(hydrated.filter(Boolean));
      },
      (error) => {
        console.error("Invitation subscription error:", error);
        if (error.code === "failed-precondition") {
          toast.warning(
            "The invitations index is missing in Firestore (helperId, status, createdAt)."
          );
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  const queueRows = useMemo(
    () =>
      openIncidents.map((problem) => ({
        problem,
        ...(matchById[problem.id] || {}),
      })),
    [openIncidents, matchById]
  );

  const confirmClaim = async () => {
    const problem = briefingProblem;
    if (!problem) return;

    setClaimingId(problem.id);
    try {
      const result = await takeJob(problem.id, currentUser);
      if (result.success) {
        setBriefingProblem(null);
        toast.success("Incident assigned to you.");
        navigate(`/workspace/${problem.id}`);
      } else {
        toast.error(result.error || "Could not claim the incident.");
      }
    } catch (error) {
      console.error("Error claiming incident:", error);
      toast.error("Could not claim the incident.");
    } finally {
      setClaimingId(null);
    }
  };

  const handleMarkResolved = useCallback(
    async (problemId) => {
      const result = await confirmResolution(problemId);
      if (result.needsReport) setPendingReportProblem(result.problem);
    },
    [confirmResolution]
  );

  const handleReportSubmitted = async (_reportId, resolutionStatus) => {
    const problem = pendingReportProblem;
    setPendingReportProblem(null);
    if (!problem) return;

    if (resolutionStatus === "unresolved") {
      await closeUnresolved(problem);
    } else {
      await finalizeResolution(problem);
    }
  };

  const handleAcceptInvitation = async (invitationId, problemId) => {
    setClaimingId(problemId);
    try {
      const result = await takeJob(problemId, currentUser);
      if (!result.success) {
        toast.error(result.error || "Could not accept the invitation.");
        return;
      }

      try {
        await updateDoc(doc(db, "invitations", invitationId), {
          status: "accepted",
          acceptedAt: serverTimestamp(),
        });
      } catch (error) {
        // The incident is already ours; a stale invitation row is cosmetic.
        console.error("Could not update the invitation:", error);
      }

      toast.success("Invitation accepted. Opening the workspace.");
      navigate(`/workspace/${problemId}`);
    } catch (error) {
      console.error("Error accepting invitation:", error);
      toast.error(`Could not accept the invitation: ${error.message}`);
    } finally {
      setClaimingId(null);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await updateDoc(doc(db, "invitations", invitationId), {
        status: "declined",
        declinedAt: serverTimestamp(),
      });
      toast.info("Invitation declined.");
    } catch (error) {
      console.error("Error declining invitation:", error);
      toast.error("Could not decline the invitation.");
    }
  };

  const metrics = [
    { id: "active", label: "Assigned to you", value: summary.activeCount },
    {
      id: "p1",
      label: "P1 open",
      value: summary.p1Count,
      tone: summary.p1Count > 0 ? "critical" : "default",
    },
    {
      id: "unanswered",
      label: "No reply yet",
      value: summary.awaitingFirstResponseCount || null,
      tone: "attention",
      hint:
        summary.oldestUnansweredMs != null
          ? `Oldest ${formatDuration(summary.oldestUnansweredMs)}`
          : undefined,
    },
    {
      id: "waiting",
      label: "Waiting on you",
      value: waitingOnViewerCount(summary, "expert") || null,
      tone: "attention",
    },
    {
      id: "oldest",
      label: "Oldest open",
      value:
        summary.oldestActiveMs != null
          ? formatDuration(summary.oldestActiveMs)
          : null,
    },
    { id: "queue", label: "Unclaimed in your lanes", value: queueRows.length },
  ];

  const tabs = [
    { id: "overview", label: "My incidents", badge: summary.activeCount },
    { id: "queue", label: "Incident queue", badge: queueRows.length },
    { id: "invitations", label: "Invitations", badge: invitations.length },
  ];

  return (
    <OperationsShell
      user={currentUser}
      roleLabel={getRoleLabel("helper")}
      historyPath="/helperhistory"
      onLogout={async () => {
        await logout();
        navigate("/");
      }}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <ClaimBriefingModal
        problem={briefingProblem}
        open={Boolean(briefingProblem)}
        claiming={claimingId === briefingProblem?.id}
        onClose={() => setBriefingProblem(null)}
        onConfirm={confirmClaim}
      />

      <OperationsSummary
        title="Your queue right now"
        subtitle={
          summary.activeCount === 0
            ? "Nothing assigned to you. Claim from the incident queue to start."
            : undefined
        }
        metrics={metrics}
        action={
          <ConversationDrawer
            problems={active}
            currentUserId={currentUser?.uid}
            open={showConversations}
            setOpen={setShowConversations}
          />
        }
      />

      {!basicInfoComplete && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-900">
            Your queue is filtered by the skills on your profile. Add them to
            start receiving matched incidents.
          </p>
          <button
            type="button"
            onClick={() => navigate("/onboarding")}
            className="whitespace-nowrap rounded-lg border border-blue-300 bg-white px-3.5 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100"
          >
            Complete onboarding
          </button>
        </div>
      )}

      {activeTab === "overview" && (
        <div className="space-y-6">
          <section className="space-y-3">
            <SectionHeading title="Active incidents" count={active.length} />
            {active.length === 0 ? (
              <EmptyPanel
                title="No incidents assigned to you"
                body="Open the incident queue to see what is unclaimed in your lanes."
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {active.map((problem) => (
                  <DashboardCard
                    key={problem.id}
                    problem={problem}
                    userRole="helper"
                    currentUserId={currentUser?.uid}
                    onMarkComplete={handleMarkResolved}
                    onRequestRefund={requestRefund}
                  />
                ))}
              </div>
            )}
          </section>

          {completed.length > 0 && (
            <section className="space-y-3">
              <SectionHeading title="Recently closed" count={completed.length} />
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {completed.map((problem) => (
                  <div key={problem.id} className="space-y-2">
                    <DashboardCard
                      problem={problem}
                      userRole="helper"
                      currentUserId={currentUser?.uid}
                      onRequestRefund={requestRefund}
                    />
                    <div className="flex items-center gap-2">
                      {problem.incidentReportId && (
                        <button
                          type="button"
                          onClick={() => setReportViewId(problem.incidentReportId)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                        >
                          View resolution report
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => dismissIncident(problem.id)}
                        className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                      >
                        Hide from dashboard
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {activeTab === "queue" && (
        <div className="space-y-3">
          <SectionHeading title="Unclaimed incidents" count={queueRows.length}>
            <p className="text-xs text-slate-500">
              Most severe first. Claiming makes the incident yours.
            </p>
          </SectionHeading>
          <IncidentQueueTable
            rows={queueRows}
            onReview={setBriefingProblem}
            claimingId={claimingId}
            emptyTitle="Nothing unclaimed in your lanes"
            emptyBody={
              expertSkills.length === 0
                ? "Add your skills in onboarding — the queue is filtered by them."
                : "Every open incident matching your skills has been claimed."
            }
          />
        </div>
      )}

      {activeTab === "invitations" && (
        <div className="space-y-3">
          <SectionHeading title="Direct requests" count={invitations.length}>
            <p className="text-xs text-slate-500">
              Customers who asked for you specifically.
            </p>
          </SectionHeading>
          {invitations.length === 0 ? (
            <EmptyPanel
              title="No pending requests"
              body="When a customer requests you by name, it appears here."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {invitations.map((invitation) => (
                <InvitationCard
                  key={invitation.id}
                  invitation={invitation}
                  busy={claimingId === invitation.problemId}
                  onAccept={handleAcceptInvitation}
                  onDecline={handleDeclineInvitation}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* The resolution report is a precondition for closing, not a follow-up. */}
      {pendingReportProblem && (
        <div className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4">
          <div className="my-8 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <IncidentReportForm
              problem={pendingReportProblem}
              specialistId={currentUser?.uid}
              onSubmitted={handleReportSubmitted}
              onClose={() => setPendingReportProblem(null)}
            />
          </div>
        </div>
      )}

      {reportViewId && (
        <div
          className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4"
          onClick={() => setReportViewId(null)}
        >
          <div
            className="my-8 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <p className="text-sm font-semibold text-slate-900">
                Resolution report
              </p>
              <button
                type="button"
                onClick={() => setReportViewId(null)}
                className="rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                Close
              </button>
            </div>
            <div className="p-5">
              <IncidentReportView reportId={reportViewId} />
            </div>
          </div>
        </div>
      )}
    </OperationsShell>
  );
}

export default DashboardHelper;
